import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ChatMessage, ChatState } from 'models/ai-chat/chat.types';
import { FeedbackRequest, QueryResponse } from 'models/ai-chat/ai-chat-response';
import { TableTransactionResponse } from 'models/ai-chat/table-pagination';

const initialState: ChatState = {
  messages: [],
  pendingMessage: false,
  error: null,
  currentConversationId: null,
  suggestions: [],
  suggestedVisualizations: [],
  suggestionText: null,
  suggestedQuestions: null,
  loading: false,
  tableRows: null,
  isDownloadingPDF: false,
  pdfDownloadError: null,
  pdfDownloadErrorMessageId: null,
  streamOrderCounter: 0
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    // Existing reducers
    setConversation: (state, action: PayloadAction<string | null>) => {
      state.currentConversationId = action.payload;
      state.messages = [];
      state.pendingMessage = false;
      state.error = null;
    },
    addMessage: (state, action: PayloadAction<ChatMessage>) => {
      state.messages.push(action.payload);
    },
    clearMessages: (state) => {
      state.messages = [];
    },
    setSuggestions: (state, action: PayloadAction<string[]>) => {
      state.suggestions = action.payload;
    },

    // New methods to manually handle API states
    setPendingMessage: (state, action: PayloadAction<{ prompt: string; isEdit?: boolean; editMessageId?: string; isRegenerate?: boolean }>) => {
      state.pendingMessage = true;
      state.error = null;
      state.streamOrderCounter = 0;

      if (action.payload.isEdit && action.payload.editMessageId) {
        // For edit mode, find the user message and update its content
        const userMessageIndex = state.messages.findIndex(msg => msg.message_id === action.payload.editMessageId);
        if (userMessageIndex !== -1) {
          // Update the user message content with new prompt
          state.messages[userMessageIndex] = {
            ...state.messages[userMessageIndex],
            content: action.payload.prompt,
            is_edited: true
          };

          // Find and clear the assistant response (next message) - clear ALL submessages
          const assistantMessageIndex = userMessageIndex + 1;
          if (assistantMessageIndex < state.messages.length) {
            const assistantMessage = state.messages[assistantMessageIndex];
            // Clear all submessages and content
            state.messages[assistantMessageIndex] = {
              ...assistantMessage,
              content: '',
              submessages: [],
              insights: undefined,
              visualizations: undefined,
              streaming_steps: [],
              visualization: {
                type: 'text',
                data: null
              },
              pending: true,
              is_streaming: true,
              error: false,
              is_edited: true
            };
          }
        }
      } else if (action.payload.isRegenerate && action.payload.editMessageId) {
        // For regenerate mode, find the assistant message and set to pending like new message
        let assistantMessageIndex = state.messages.findIndex(msg => msg.message_id === action.payload.editMessageId);

        // If not found in top-level, search in submessages
        if (assistantMessageIndex === -1) {
          for (let i = 0; i < state.messages.length; i++) {
            const msg = state.messages[i];
            if (msg.submessages && msg.submessages.length > 0) {
              const submessageIndex = msg.submessages.findIndex(sub => sub.message_id === action.payload.editMessageId);
              if (submessageIndex !== -1) {
                // Found in submessages - clear ALL submessages for the parent message
                state.messages[i].submessages = [];
                state.messages[i].insights = undefined;
                state.messages[i].visualizations = undefined;
                state.messages[i].content = '';
                state.messages[i].pending = true;
                state.messages[i].is_streaming = true;
                state.messages[i].streaming_steps = [];
                state.messages[i].visualization = {
                  type: 'text',
                  data: null
                };
                break;
              }
            }
          }
        } else if (assistantMessageIndex !== -1) {
          // Found in top-level messages, clear all content and set to pending
          state.messages[assistantMessageIndex] = {
            role: 'assistant',
            content: '',
            created_at: new Date().toISOString(),
            message_id: action.payload.editMessageId,
            sql_query: '',
            visualization: {
              type: 'text',
              data: null
            },
            feedback_type: null,
            feedback_value: null,
            metadata: null,
            insights: undefined,
            visualizations: undefined,
            submessages: undefined,
            streaming_steps: [],
            pending: true,
            is_streaming: true,
            error: false
          };
        }
      } else {
        // For new messages, add user message and pending assistant message
        state.messages.push({
          role: 'user',
          content: action.payload.prompt,
          created_at: new Date().toISOString(),
          message_id: `temp-${Date.now()}`,
          sql_query: '',
          feedback_type: null,
          feedback_value: null,
          metadata: null
        });

        // Add pending assistant message
        state.messages.push({
          role: 'assistant',
          content: '',
          created_at: new Date().toISOString(),
          message_id: `pending-${Date.now()}`,
          pending: true,
          sql_query: '',
          feedback_type: null,
          feedback_value: null,
          metadata: null
        });
      }
    },

    setMessageSuccess: (state, action: PayloadAction<QueryResponse>) => {
      state.pendingMessage = false;

      const response = action.payload;

      // Update conversation ID if not set
      if (!state.currentConversationId) {
        state.currentConversationId = response.conversation_id;
      }

      // Check if this is an edit response (message already exists)
      // First try to find by exact message_id match
      let existingMessageIndex = state.messages.findIndex(msg => msg.message_id === response.message_id);
      const existingParentMessageIndex = state.messages.findIndex(msg => msg.role === 'user' && msg.message_id.includes('temp'));

      if (existingMessageIndex === -1) {
        existingMessageIndex = state.messages.findIndex(msg => msg.pending === true && msg.role === 'assistant');
      }

      if (existingParentMessageIndex !== -1 && response.parent_message_id && String(response.parent_message_id).trim().length > 0) {
        const existingMessage = state.messages[existingParentMessageIndex];
        state.messages[existingParentMessageIndex] = {
          ...existingMessage,
          message_id: response.parent_message_id
        };
      }

      if (response.error) {
        state.error = response.error;
        const errorMessage = {
          role: 'assistant' as const,
          content: `Please contact AJ for further assistance`,
          created_at: new Date().toISOString(),
          message_id: response.message_id,
          sql_query: response.sql_query,
          visualization: {
            type: 'text',
            data: null
          },
          error: true,
          feedback_type: null,
          feedback_value: null,
          metadata: response.metadata
        };

        if (existingMessageIndex !== -1) {
          // Replace existing message
          state.messages[existingMessageIndex] = errorMessage;
        } else {
          // Add new message
          state.messages.push(errorMessage);
        }

        state.suggestions = [];
        state.suggestedQuestions = [];
        state.suggestedVisualizations = [];
      } else {
        // Map insights_data array to insights object with proper null/undefined handling
        let insights: { summary_insights?: string; dimension_insights?: string; time_insights?: string; report_insights?: any[] } | undefined = undefined;

        if (response?.insights_data && Array.isArray(response.insights_data) && response.insights_data.length > 0) {
          insights = {
            summary_insights: undefined,
            dimension_insights: undefined,
            time_insights: undefined,
            report_insights: undefined
          };

          response.insights_data.forEach(insight => {
            // Check if insight object and properties exist
            if (insight && insight.insight_type) {
              switch (insight.insight_type) {
                case 'summary_insights':
                  if (insight.insights) {
                    insights!.summary_insights = insight.insights;
                  }
                  break;
                case 'dimension_insights':
                  if (insight.insights) {
                    insights!.dimension_insights = insight.insights;
                  }
                  break;
                case 'time_insights':
                  if (insight.insights) {
                    insights!.time_insights = insight.insights;
                  }
                  break;
                case 'report_insights':
                  if (insight.report_data && insight.report_data.length > 0) {
                    insights!.report_insights = insight.report_data;
                  }
                  break;
                default:
                  console.warn('Unknown insight type:', insight.insight_type);
              }
            } else {
              console.warn('Invalid insight object:', insight);
            }
          });
        }

        const safeMessageId = (response.message_id && String(response.message_id).trim().length > 0)
          ? response.message_id
          : `msg-${Date.now()}-${Math.random().toString(36).slice(2)}`;

        const assistantMessage = {
          role: 'assistant' as const,
          content: response.text,
          created_at: new Date().toISOString(),
          message_id: safeMessageId,
          sql_query: response.sql_query,
          visualization: {
            type: response.visualization,
            data: response.data,
          },
          feedback_type: null,
          feedback_value: null,
          metadata: response.metadata,
          insights: insights,
          streaming_steps: undefined as any,
          visualizations: undefined as any,
          is_streaming: true,
          report_suggestions: (response as any).report_suggestions && Array.isArray((response as any).report_suggestions)
            ? (response as any).report_suggestions
            : undefined,
          parent_message_id: response.parent_message_id || null
        };

        const pendingIndex = state.messages.findIndex(msg => msg.pending === true && msg.role === 'assistant');
        if (pendingIndex !== -1) {
          if (state.messages[pendingIndex].streaming_steps) {
            assistantMessage.streaming_steps = state.messages[pendingIndex].streaming_steps;
          }
          if (state.messages[pendingIndex].visualizations) {
            assistantMessage.visualizations = state.messages[pendingIndex].visualizations;
          }
        }

        if (existingMessageIndex !== -1) {
          // Replace existing message (for edits) - preserve existing feedback, streaming steps, and visualizations
          const existingMessage = state.messages[existingMessageIndex];
          state.messages[existingMessageIndex] = {
            ...assistantMessage,
            feedback_type: existingMessage.feedback_type,
            feedback_value: existingMessage.feedback_value,
            streaming_steps: assistantMessage.streaming_steps || existingMessage.streaming_steps,
            visualizations: assistantMessage.visualizations || existingMessage.visualizations,
            report_suggestions: assistantMessage.report_suggestions || (existingMessage as any).report_suggestions,
            is_streaming: true
          };
        } else {
          // Add new message (for new queries)
          state.messages.push(assistantMessage);
        }

        if (pendingIndex !== -1 && pendingIndex !== existingMessageIndex) {
          state.messages.splice(pendingIndex, 1);
        }

        // Process suggestions
        if (response.suggestiontext) {
          const suggestions = response.suggestiontext
            .split('?')
            .map((s: any) => s.trim())
            .filter((s: any) => s.length > 0)
            .map((s: any) => s + '?');

          state.suggestions = suggestions;
        }

        // Process suggested questions
        if (response.suggested_questions && response.suggested_questions.length > 0) {
          state.suggestedQuestions = state.suggestions.concat(response.suggested_questions);
        }

        // Process suggested visualizations
        if (response.suggested_visualizations && response.suggested_visualizations.length > 0) {
          state.suggestedVisualizations = response.suggested_visualizations;
        }
      }
    },


    loadExistingMessageSuccess: (state, action: PayloadAction<QueryResponse[]>) => {
      state.pendingMessage = false;

      state.messages = state.messages.filter((msg: any) => !msg.pending);
      const response = action.payload;

      const processMessage = (element: any): ChatMessage => {
        const elementMessageId = (element?.message_id && String(element.message_id).trim().length > 0)
          ? element.message_id
          : `msg-${Date.now()}-${Math.random().toString(36).slice(2)}`;

        let insights: { summary_insights?: string; dimension_insights?: string; time_insights?: string; report_insights?: any[] } | undefined = undefined;

        if (element?.insights_data && Array.isArray(element.insights_data) && element.insights_data.length > 0) {
          insights = {
            summary_insights: undefined,
            dimension_insights: undefined,
            time_insights: undefined,
            report_insights: undefined
          };

          element.insights_data.forEach((insight: any) => {
            if (insight && insight.insight_type) {
              switch (insight.insight_type) {
                case 'summary_insights':
                  if (insight.insights) {
                    insights!.summary_insights = insight.insights;
                  }
                  break;
                case 'dimension_insights':
                  if (insight.insights) {
                    insights!.dimension_insights = insight.insights;
                  }
                  break;
                case 'time_insights':
                  if (insight.insights) {
                    insights!.time_insights = insight.insights;
                  }
                  break;
                case 'report_insights':
                  if (insight.report_data && insight.report_data.length > 0) {
                    insights!.report_insights = insight.report_data;
                  }
                  break;
                default:
                  console.warn('Unknown insight type:', insight.insight_type);
              }
            } else {
              console.warn('Invalid insight object:', insight);
            }
          });
        }

        const parsedData = (element?.data && element.data !== null && element.data !== '') ?
          (() => {
            try {
              if (typeof element.data === 'string') {
                if (element.data.trim() === '') {
                  return null;
                }
                if (element.data === '[]' && element.visualization === 'table') {
                  return { headers: [], rows: [] };
                }
                return JSON.parse(element.data);
              }
              return element.data;
            } catch (error) {
              console.warn('Failed to parse visualization data:', error);
              return null;
            }
          })() : null;

        const baseMessage: ChatMessage = {
          role: (element?.role === 'user') ? 'user' : 'assistant',
          content: element?.text || '',
          created_at: element?.created_at || new Date().toISOString(),
          message_id: elementMessageId,
          sql_query: element?.sql_query || '',
          visualization: {
            type: element?.visualization || 'text',
            data: parsedData,
          },
          feedback_type: (element?.feedback?.type) ? element.feedback.type : null,
          feedback_value: (element?.feedback?.value) ? element.feedback.value : null,
          metadata: element?.metadata || null,
          insights: insights,
          report_suggestions: element?.report_suggestions && Array.isArray(element.report_suggestions) && element.report_suggestions.length > 0
            ? element.report_suggestions
            : undefined,
          llm_payload: element?.llm_payload || undefined
        };

        const responsesArray = element?.responses && Array.isArray(element.responses) && element.responses.length > 0
          ? element.responses
          : element?.submessages && Array.isArray(element.submessages) && element.submessages.length > 0
            ? element.submessages
            : [];

        if (responsesArray.length > 0) {
          baseMessage.submessages = responsesArray.map((sub: any) => {
            const subMessage = processMessage(sub);
            if (sub?.visualization && sub.visualization !== null) {
              let subParsedData = null;

              if (sub?.data !== null && sub?.data !== undefined && sub?.data !== '') {
                if (typeof sub.data === 'string') {
                  if (sub.data.trim() === '') {
                    subParsedData = null;
                  } else if (sub.data === '[]' && sub.visualization === 'table') {
                    subParsedData = { headers: [], rows: [] };
                  } else {
                    try {
                      subParsedData = JSON.parse(sub.data);
                    } catch (error) {
                      console.warn('Failed to parse submessage visualization data:', error);
                      subParsedData = null;
                    }
                  }
                } else {
                  subParsedData = sub.data;
                }
              } else if (sub.visualization === 'table') {
                subParsedData = { headers: [], rows: [] };
              }

              if (subParsedData !== null || sub.visualization === 'table') {
                subMessage.visualization = {
                  type: sub.visualization,
                  data: subParsedData || (sub.visualization === 'table' ? { headers: [], rows: [] } : null)
                };
              }
            }
            if (sub?.sql_query) {
              subMessage.sql_query = sub.sql_query;
            }
            if (sub?.metadata) {
              subMessage.metadata = sub.metadata;
            }
            // Map llm_payload for ChartSwitcher support
            if (sub?.llm_payload) {
              (subMessage as any).llm_payload = sub.llm_payload;
            }
            // Preserve clarifications in submessage
            if (sub?.clarifications && Array.isArray(sub.clarifications)) {
              (subMessage as any).clarifications = sub.clarifications;
            }
            return subMessage;
          });

          if (baseMessage.submessages && baseMessage.submessages.length > 0) {
            const lastSubmessage = baseMessage.submessages[baseMessage.submessages.length - 1];
            if (lastSubmessage && lastSubmessage.insights) {
              baseMessage.insights = lastSubmessage.insights;
            }
          }

          const allReportSuggestions: any[] = [];
          if (baseMessage.report_suggestions && Array.isArray(baseMessage.report_suggestions)) {
            allReportSuggestions.push(...baseMessage.report_suggestions);
          }

          if (baseMessage.submessages) {
            baseMessage.submessages.forEach((sub: any) => {
              if (sub.report_suggestions && Array.isArray(sub.report_suggestions) && sub.report_suggestions.length > 0) {
                sub.report_suggestions.forEach((report: any) => {
                  const exists = allReportSuggestions.some((r: any) => r.report_id === report.report_id);
                  if (!exists) {
                    allReportSuggestions.push(report);
                  }
                });
              }
            });
          }

          if (allReportSuggestions.length > 0) {
            (baseMessage as any).report_suggestions = allReportSuggestions;
          }

          const allClarifications: any[] = [];
          if (baseMessage.submessages) {
            baseMessage.submessages.forEach((sub: any) => {
              if ((sub as any).clarifications && Array.isArray((sub as any).clarifications) && (sub as any).clarifications.length > 0) {
                allClarifications.push(...(sub as any).clarifications);
              }
            });
          }
          
          if (allClarifications.length > 0) {
            (baseMessage as any).clarifications = allClarifications;
          }

          // Build visualizations array from submessages for ChartSwitcher support
          if (baseMessage.submessages && baseMessage.submessages.length > 0) {
            const visualizations: any[] = [];
            baseMessage.submessages.forEach((sub: any, index: number) => {
              if (sub.visualization && sub.visualization.type && sub.visualization.data) {
                visualizations.push({
                  type: sub.visualization.type,
                  data: sub.visualization.data,
                  sql_query: sub.sql_query,
                  metadata: sub.metadata,
                  llm_payload: sub.llm_payload,
                  timestamp: Date.now(),
                  order: index
                });
              }
            });
            if (visualizations.length > 0) {
              (baseMessage as any).visualizations = visualizations;
            }
          }
        }

        return baseMessage;
      };

      if (response != null && response.length > 0) {
        response.forEach(element => {
          state.messages.push(processMessage(element));
        });
      }

    },

    setMessageFailure: (state, action: PayloadAction<string>) => {
      state.pendingMessage = false;
      state.error = action.payload || 'Failed to send message';

      // Remove pending messages
      state.messages = state.messages.filter((msg: any) => !msg.pending);

      // Check if we already have an error message for this same error to prevent duplicates
      const existingError = state.messages.find((msg: any) =>
        msg.error && msg.content === state.error &&
        Date.now() - new Date(msg.created_at).getTime() < 5000 // Within last 5 seconds
      );

      // Only add error message if we don't have a recent duplicate
      if (!existingError) {
        state.messages.push({
          role: 'assistant',
          content: state.error,
          created_at: new Date().toISOString(),
          message_id: `error-${Date.now()}`,
          error: true,
          sql_query: '',
          feedback_type: null,
          feedback_value: null,
          metadata: null
        });
      }
    },

    addMessageFeedback: (state, action: PayloadAction<FeedbackRequest>) => {
      const { message_id, feedback_type, feedback_value, state_message_id, parent_message_id } = action.payload;
      
      // Strategy 1: Try to find by state_message_id (works for streaming - direct message_id match)
      let messageIndex = state.messages.findIndex(
        msg => msg.message_id === state_message_id
      );
      
      // Strategy 2: If not found, try by parent_message_id (works for loaded messages)
      if (messageIndex === -1 && parent_message_id) {
        messageIndex = state.messages.findIndex(
          msg => msg.message_id === parent_message_id || msg.parent_message_id === parent_message_id
        );
      }
      
      // Strategy 3: Fallback to message_id (API ID)
      if (messageIndex === -1) {
        messageIndex = state.messages.findIndex(
          msg => msg.message_id === message_id || msg.parent_message_id === message_id
        );
      }

      if (messageIndex !== -1) {
        state.messages[messageIndex] = {
          ...state.messages[messageIndex],
          feedback_type: feedback_type,
          feedback_value: feedback_value
        };
      }
    },

    removeMessageFeedback: (state, action: PayloadAction<FeedbackRequest>) => {
      const { message_id, state_message_id, parent_message_id } = action.payload;
      
      // Strategy 1: Try to find by state_message_id
      let messageIndex = state.messages.findIndex(
        msg => msg.message_id === state_message_id
      );
      
      // Strategy 2: If not found, try by parent_message_id
      if (messageIndex === -1 && parent_message_id) {
        messageIndex = state.messages.findIndex(
          msg => msg.message_id === parent_message_id || msg.parent_message_id === parent_message_id
        );
      }
      
      // Strategy 3: Fallback to message_id
      if (messageIndex === -1) {
        messageIndex = state.messages.findIndex(
          msg => msg.message_id === message_id || msg.parent_message_id === message_id
        );
      }

      if (messageIndex !== -1) {
        state.messages[messageIndex] = {
          ...state.messages[messageIndex],
          feedback_type: null,
          feedback_value: null
        };
      }
    },
    setTablePaginatedDataSet: (state, action: PayloadAction<TableTransactionResponse>) => {
      state.tableRows = action.payload;
    },

    /**
     * Updates a message with new data from filter updates
     * @param state - The current chat state
     * @param action - Action containing messageId and newData
     */
    updateMessageData: (state, action: PayloadAction<{ messageId: string; newData: any }>) => {
      const { messageId, newData } = action.payload;

      const messageIndex = state.messages.findIndex(
        msg => msg.message_id === messageId
      );

      if (messageIndex !== -1) {
        // Check if this is a full message update (for editing) or just data update
        if (newData.text !== undefined) {
          // Full message update for editing
          state.messages[messageIndex] = {
            ...state.messages[messageIndex],
            content: newData.text,
            sql_query: newData.sql_query,
            metadata: newData.metadata,
            visualization: {
              type: newData.visualization || 'text',
              data: newData.data
            },
            pending: false,
            error: false,
            insights: undefined // Clear insights when editing
          };
        } else {
          // Partial data update for filters
          if (!state.messages[messageIndex].visualization) {
            state.messages[messageIndex].visualization = { type: 'table', data: null };
          }

          if (newData.data == null) {
            state.messages[messageIndex].visualization!.data = null;
            state.messages[messageIndex].sql_query = newData.sql_query;
            state.messages[messageIndex].filter_text = newData.text;

          } else {
            state.messages[messageIndex].visualization!.data = newData.data;
            state.messages[messageIndex].visualization!.type = newData.visualization || state.messages[messageIndex].visualization!.type;
            state.messages[messageIndex].sql_query = newData.sql_query;
            state.messages[messageIndex].metadata = newData.metadata;

            // Update insights if provided
            if (newData.insights) {
              state.messages[messageIndex].insights = newData.insights;
            }
          }
        }
      }
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    setPdfDownloadLoading: (state, action: PayloadAction<boolean>) => {
      state.isDownloadingPDF = action.payload;
      if (action.payload) {
        state.pdfDownloadError = null;
        state.pdfDownloadErrorMessageId = null;
      }
    },

    setPdfDownloadError: (state, action: PayloadAction<{ error: string | null; messageId: string | null }>) => {
      state.pdfDownloadError = action.payload.error;
      state.pdfDownloadErrorMessageId = action.payload.messageId;
      state.isDownloadingPDF = false;
    },
    addInsightToMessage: (state, action: PayloadAction<{ messageId: string; insightType: string; insight: string, report_data: any[] }>) => {
      const { messageId, insightType, insight } = action.payload;
      const messageIndex = state.messages.findIndex(msg => msg.message_id === messageId);

      if (messageIndex !== -1) {
        // Initialize insights object if it doesn't exist
        if (!state.messages[messageIndex].insights) {
          state.messages[messageIndex].insights = {};
        }

        if (action.payload.report_data && action.payload.report_data.length > 0) {
          state.messages[messageIndex].insights!.report_insights = action.payload.report_data;
        }

        // Add the insight based on type
        if (insightType === 'summary_insights') {
          state.messages[messageIndex].insights!.summary_insights = insight;
        } else if (insightType === 'dimension_insights') {
          state.messages[messageIndex].insights!.dimension_insights = insight;
        } else if (insightType === 'time_insights') {
          state.messages[messageIndex].insights!.time_insights = insight;
        }
      }
    },

    addStreamingStep: (state, action: PayloadAction<{
      type: string;
      step: string;
      status: 'starting' | 'completed' | 'error';
      message: string;
      timestamp: number;
      details?: any;
      context?: any;
    }>) => {
      // Filter out report_suggestions steps - don't show them to users
      if (action.payload.type === 'report_suggestions') {
        return;
      }

      let targetMessageIndex = state.messages.findIndex(msg => msg.pending === true && msg.role === 'assistant');

      if (targetMessageIndex === -1) {
        targetMessageIndex = state.messages.findIndex(msg => msg.is_streaming === true && msg.role === 'assistant');
      }

      if (targetMessageIndex === -1) {
        const assistantMessages = state.messages.filter(msg => msg.role === 'assistant');
        if (assistantMessages.length > 0) {
          targetMessageIndex = state.messages.findIndex(msg => msg.message_id === assistantMessages[assistantMessages.length - 1].message_id);
        }
      }

      if (targetMessageIndex !== -1) {
        if (!state.messages[targetMessageIndex].streaming_steps) {
          state.messages[targetMessageIndex].streaming_steps = [];
        }
        if (!state.messages[targetMessageIndex].is_streaming) {
          state.messages[targetMessageIndex].is_streaming = true;
        }

        const normalizeMessage = (msg: string) => {
          return msg.replace(/[\u{1F300}-\u{1F9FF}]/gu, '').replace(/[\u{2705}\u{26a1}]/gu, '').trim();
        };

        const normalizedNewMessage = normalizeMessage(action.payload.message);
        const newStepKey = `${action.payload.step}:${normalizedNewMessage}`;

        const existingStepIndex = state.messages[targetMessageIndex].streaming_steps!.findIndex((step: any) => {
          const normalizedExistingMessage = normalizeMessage(step.message);
          const existingStepKey = `${step.step}:${normalizedExistingMessage}`;
          return existingStepKey === newStepKey;
        });

        if (existingStepIndex !== -1) {
          const existingStep = state.messages[targetMessageIndex].streaming_steps![existingStepIndex];
          const existingOrder = (existingStep as any).order;
          state.messages[targetMessageIndex].streaming_steps![existingStepIndex] = {
            ...action.payload,
            timestamp: Math.max(existingStep.timestamp, action.payload.timestamp),
            order: existingOrder !== undefined ? existingOrder : state.streamOrderCounter++
          } as any;
        } else {
          state.messages[targetMessageIndex].streaming_steps!.push({
            ...action.payload,
            order: state.streamOrderCounter++
          } as any);
        }
      }
    },

    addVisualizationResult: (state, action: PayloadAction<{
      type: string;
      data: any;
      sql_query?: string;
      question?: string;
      metadata?: any;
      timestamp?: number;
      report_suggestions?: any[];
      llm_payload?: any;
    }>) => {
      let targetMessageIndex = state.messages.findIndex(msg => msg.pending === true && msg.role === 'assistant');

      if (targetMessageIndex === -1) {
        targetMessageIndex = state.messages.findIndex(msg => msg.is_streaming === true && msg.role === 'assistant');
      }

      if (targetMessageIndex === -1) {
        const assistantMessages = state.messages.filter(msg => msg.role === 'assistant');
        if (assistantMessages.length > 0) {
          targetMessageIndex = state.messages.findIndex(msg => msg.message_id === assistantMessages[assistantMessages.length - 1].message_id);
        }
      }

      if (targetMessageIndex !== -1) {
        if (!state.messages[targetMessageIndex].visualizations) {
          state.messages[targetMessageIndex].visualizations = [];
        }

        const visualizationData = {
          ...action.payload,
          timestamp: action.payload.timestamp || Date.now(),
          order: state.streamOrderCounter++
        } as any;

        // If report_suggestions is in the payload, add it to the visualization
        if (action.payload.report_suggestions && Array.isArray(action.payload.report_suggestions)) {
          visualizationData.report_suggestions = action.payload.report_suggestions;
        }

        state.messages[targetMessageIndex].visualizations!.push(visualizationData);

        // Also add report_suggestions to message level if provided
        if (action.payload.report_suggestions && Array.isArray(action.payload.report_suggestions)) {
          const existingReports = (state.messages[targetMessageIndex] as any).report_suggestions || [];
          const newReports = action.payload.report_suggestions.filter((newReport: any) => {
            return !existingReports.some((existing: any) =>
              (existing.report_id && newReport.report_id && existing.report_id === newReport.report_id) ||
              (existing.report_url && newReport.report_url && existing.report_url === newReport.report_url)
            );
          });
          if (newReports.length > 0) {
            (state.messages[targetMessageIndex] as any).report_suggestions = [...existingReports, ...newReports];
          }
        }
      }
    },

    stopStreaming: (state) => {
      state.pendingMessage = false;
      const assistantMessages = state.messages.filter(msg => msg.role === 'assistant');
      if (assistantMessages.length > 0) {
        const lastAssistantIndex = state.messages.findIndex(msg => msg.message_id === assistantMessages[assistantMessages.length - 1].message_id);
        if (lastAssistantIndex !== -1) {
          state.messages[lastAssistantIndex].pending = false;
          state.messages[lastAssistantIndex].is_streaming = false;
        }
      }
    },

    addReportSuggestions: (state, action: PayloadAction<{ messageId?: string; reportSuggestions: any[] }>) => {
      const { messageId, reportSuggestions } = action.payload;

      let targetMessageIndex = -1;

      if (messageId) {
        targetMessageIndex = state.messages.findIndex(msg => msg.message_id === messageId);
      }

      if (targetMessageIndex === -1) {
        targetMessageIndex = state.messages.findIndex(msg => msg.pending === true && msg.role === 'assistant');
      }

      if (targetMessageIndex === -1) {
        targetMessageIndex = state.messages.findIndex(msg => msg.is_streaming === true && msg.role === 'assistant');
      }

      if (targetMessageIndex === -1) {
        const assistantMessages = state.messages.filter(msg => msg.role === 'assistant');
        if (assistantMessages.length > 0) {
          targetMessageIndex = state.messages.findIndex(msg => msg.message_id === assistantMessages[assistantMessages.length - 1].message_id);
        }
      }

      if (targetMessageIndex !== -1 && reportSuggestions && Array.isArray(reportSuggestions) && reportSuggestions.length > 0) {
        (state.messages[targetMessageIndex] as any).report_suggestions = reportSuggestions;
      }
    },

  }
});

export const {
  setConversation,
  addMessage,
  clearMessages,
  setSuggestions,
  setPendingMessage,
  setMessageSuccess,
  loadExistingMessageSuccess,
  setMessageFailure,
  addMessageFeedback,
  removeMessageFeedback,
  setTablePaginatedDataSet,
  setPdfDownloadLoading,
  setPdfDownloadError,
  updateMessageData,
  setLoading,
  addInsightToMessage,
  addStreamingStep,
  addVisualizationResult,
  stopStreaming,
  addReportSuggestions
} = chatSlice.actions;
export default chatSlice.reducer;